import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../api';
import '../pages/styles.css';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STEPS = [
  [1, 'Navn'],
  [2, 'Tilslut'],
  [3, 'Godkendelser'],
  [4, 'Færdig'],
];

const POLL_INTERVAL_MS = 3000;

// Each consent runs in its own tab (see the window.open comment below) —
// this modal never navigates itself, so the only way to know a consent
// finished is to poll /api/tenants for the field it's expected to set,
// same list Tenants.jsx itself reads. Per direct feedback: two
// independent, clearly labelled rows with their own status, instead of
// silently auto-chaining into a second Microsoft window with no
// explanation (that read as a stuck/broken flow and got abandoned).
export default function AddTenantModal({ open, onClose, orgId }) {
  const [step, setStep] = useState(1);
  const [friendlyName, setFriendlyName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [error, setError] = useState('');
  const [graphStatus, setGraphStatus] = useState('idle'); // idle | waiting | done
  const [exchangeStatus, setExchangeStatus] = useState('locked'); // locked | idle | waiting | done | skipped
  const [exchangeError, setExchangeError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  if (!open) return null;

  function stopPolling() {
    clearInterval(pollRef.current);
    pollRef.current = null;
  }

  function handleClose() {
    stopPolling();
    setStep(1);
    setFriendlyName('');
    setTenantId('');
    setError('');
    setExchangeError('');
    setGraphStatus('idle');
    setExchangeStatus('locked');
    onClose();
  }

  function goToStep2(e) {
    e.preventDefault();
    if (!friendlyName.trim()) {
      setError('Udfyld tenant-navnet');
      return;
    }
    setError('');
    setStep(2);
  }

  function goToStep3(e) {
    e.preventDefault();
    if (!GUID_REGEX.test(tenantId.trim())) {
      setError('Tenant ID er ikke et gyldigt GUID');
      return;
    }
    setError('');
    setStep(3);
  }

  // Polls /api/tenants until `check` returns true for the matching tenant,
  // then runs `onDone`. Same list Tenants.jsx reads — no separate endpoint
  // needed just for this.
  function pollUntil(check, onDone) {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/tenants${orgId ? `?orgId=${orgId}` : ''}`);
        if (!res.ok) return;
        const list = await res.json();
        const match = Array.isArray(list) ? list.find((t) => t.tenantId === tenantId.trim()) : null;
        if (match && check(match)) {
          stopPolling();
          onDone(match);
        }
      } catch {
        // transient — next tick tries again
      }
    }, POLL_INTERVAL_MS);
  }

  async function startGraphConsent() {
    setError('');
    setGraphStatus('waiting');
    try {
      const res = await apiFetch('/auth/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: tenantId.trim(),
          friendlyName: friendlyName.trim(),
          ...(orgId ? { orgId } : {}),
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Kunne ikke starte godkendelsen');
      }
      const { authUrl } = await res.json();
      // New tab, not window.location — Edge treats login.microsoftonline.com
      // specially and can force-switch to a different, Microsoft-linked
      // browser profile mid-navigation, which left this modal's own tab on
      // a broken session (reported live). This tab just waits and polls.
      window.open(authUrl, '_blank', 'noopener');
      pollUntil(() => true, () => {
        setGraphStatus('done');
        setExchangeStatus('idle');
      });
    } catch (err) {
      setError(err.message);
      setGraphStatus('idle');
    }
  }

  async function startExchangeConsent() {
    setExchangeError('');
    setExchangeStatus('waiting');
    try {
      const res = await apiFetch('/api/exchange-connect-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenantId.trim() }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Kunne ikke starte Exchange-forbindelsen');
      }
      const { authUrl } = await res.json();
      window.open(authUrl, '_blank', 'noopener');
      pollUntil((t) => !!t.exchangeConnectedAt, () => setExchangeStatus('done'));
    } catch (err) {
      setExchangeError(err.message);
      setExchangeStatus('idle');
    }
  }

  function skipExchange() {
    stopPolling();
    setExchangeStatus('skipped');
  }

  const graphDone = graphStatus === 'done';
  const exchangeDone = exchangeStatus === 'done' || exchangeStatus === 'skipped';

  return (
    <div className="modal-overlay" onClick={() => step !== 3 && handleClose()}>
      <div className="tenant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tenant-modal__rail">
          <div className="tenant-modal__brand"><span className="tenant-modal__brand-dot" />Tilføj tenant</div>
          <div className="tenant-rail-steps">
            {STEPS.map(([num, label]) => (
              <div key={num} className={`tenant-rail-step ${step === num ? 'is-active' : step > num ? 'is-done' : ''}`}>
                <span className="tenant-rail-step__dot">{step > num ? '✓' : num}</span>
                {label}
              </div>
            ))}
          </div>
        </div>

        <div className="tenant-modal__body">
          <button className="modal-panel__close" onClick={handleClose} aria-label="Luk">×</button>

          {step === 1 && (
            <form onSubmit={goToStep2}>
              <div className="tenant-modal__head">
                <h2>Hvad hedder tenanten?</h2>
                <p>Navnet vises i jeres tenant-liste — behøver ikke matche kundens officielle firmanavn.</p>
              </div>
              <div className="form-field">
                <label>Tenant-navn</label>
                <input value={friendlyName} onChange={(e) => setFriendlyName(e.target.value)} autoFocus />
              </div>
              {error && <p className="modal-msg modal-msg--error">{error}</p>}
              <div className="tenant-modal__foot">
                <button type="button" className="btn-secondary" onClick={handleClose}>Annullér</button>
                <button type="submit" className="btn-primary">Fortsæt →</button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={goToStep3}>
              <div className="tenant-modal__head">
                <h2>Hvilken tenant?</h2>
                <p>Tenant-ID'et fortæller Microsoft præcis hvilken kunde godkendelsen i næste trin gælder.</p>
              </div>
              <div className="form-field">
                <label>Tenant ID (Azure GUID)</label>
                <input value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" autoFocus />
                <div className="form-hint">Findes i kundens Microsoft 365 admin-center under "Indstillinger → Organisationsprofil", eller i Azure Portal → Entra ID → Properties.</div>
              </div>
              {error && <p className="modal-msg modal-msg--error">{error}</p>}
              <div className="tenant-modal__foot">
                <button type="button" className="btn-secondary" onClick={() => { setError(''); setStep(1); }}>← Tilbage</button>
                <button type="submit" className="btn-primary">Fortsæt →</button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div>
              <div className="tenant-modal__head">
                <h2>Godkend adgang for {friendlyName}</h2>
                <p>To uafhængige godkendelser. Hver åbner i en ny fane — dette vindue venter og opdaterer status automatisk.</p>
              </div>

              <div className="consent-list">
                <div className={`consent-item ${graphDone ? 'is-done' : ''}`}>
                  <div className="consent-item__row">
                    <div className="consent-item__badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                    </div>
                    <div className="consent-item__main">
                      <div className="consent-item__title">Microsoft Graph <small>Kerne</small></div>
                      <div className="consent-item__desc">Læser tenantens sikkerhedsdata — brugere, enheder, grupper, sikkerhed m.fl.</div>
                    </div>
                    <div className="consent-item__action">
                      {graphStatus === 'done' && <span className="status-pill status-pill--done"><CheckIcon />Godkendt</span>}
                      {graphStatus === 'waiting' && <span className="status-pill status-pill--waiting"><SpinIcon />Afventer</span>}
                      <div style={{ height: 6 }} />
                      {graphStatus === 'done' && <button className="btn-consent" disabled>Godkendt ✓</button>}
                      {graphStatus === 'waiting' && <button className="btn-consent is-waiting" disabled>Afventer…</button>}
                      {graphStatus === 'idle' && <button className="btn-consent" onClick={startGraphConsent}>Godkend →</button>}
                    </div>
                  </div>
                  {graphStatus === 'waiting' && (
                    <div className="consent-item__note">
                      <span>💬</span>
                      <span>Godkendelsen åbnede i en ny fane — vend tilbage hertil når du har bekræftet i den. Denne status opdaterer sig selv.</span>
                    </div>
                  )}
                </div>

                <div className={`consent-item ${exchangeDone ? 'is-done' : ''} ${exchangeStatus === 'locked' ? 'is-locked' : ''}`}>
                  <div className="consent-item__row">
                    <div className="consent-item__badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /></svg>
                    </div>
                    <div className="consent-item__main">
                      <div className="consent-item__title">Exchange Online <small>Postkasse-typer</small></div>
                      <div className="consent-item__desc">Skelner delte postkasser, mødelokaler og udstyr fra rigtige medarbejdere. Kræver at Microsoft Graph er godkendt først.</div>
                    </div>
                    <div className="consent-item__action">
                      {exchangeStatus === 'done' && <span className="status-pill status-pill--done"><CheckIcon />Godkendt</span>}
                      {exchangeStatus === 'waiting' && <span className="status-pill status-pill--waiting"><SpinIcon />Afventer</span>}
                      {exchangeStatus === 'locked' && <span className="status-pill status-pill--locked">Låst</span>}
                      {exchangeStatus === 'skipped' && <span className="status-pill status-pill--locked">Sprunget over</span>}
                      <div style={{ height: 6 }} />
                      {exchangeStatus === 'locked' && <button className="btn-consent" disabled>Godkend →</button>}
                      {exchangeStatus === 'idle' && <button className="btn-consent" onClick={startExchangeConsent}>Godkend →</button>}
                      {exchangeStatus === 'waiting' && <button className="btn-consent is-waiting" disabled>Afventer…</button>}
                      {(exchangeStatus === 'done' || exchangeStatus === 'skipped') && <button className="btn-consent" disabled>{exchangeStatus === 'done' ? 'Godkendt ✓' : 'Sprunget over'}</button>}
                    </div>
                  </div>
                  {exchangeStatus === 'waiting' && (
                    <div className="consent-item__note">
                      <span>💬</span>
                      <span>En Exchange- eller Global Administrator hos kunden skal godkende. Vend tilbage hertil bagefter — status opdaterer sig selv.</span>
                    </div>
                  )}
                  {exchangeError && <p className="modal-msg modal-msg--error" style={{ margin: '0 18px 12px' }}>{exchangeError}</p>}
                  {(exchangeStatus === 'idle' || exchangeStatus === 'waiting') && (
                    <div className="consent-item__skip">
                      <button type="button" onClick={skipExchange}>Spring over, gør det senere fra redigér-panelet →</button>
                    </div>
                  )}
                </div>
              </div>

              {error && <p className="modal-msg modal-msg--error">{error}</p>}
              <div className="tenant-modal__foot">
                <button type="button" className="btn-secondary" onClick={() => setStep(2)}>← Tilbage</button>
                <button type="button" className="btn-primary" disabled={!graphDone} onClick={() => setStep(4)}>Færdig →</button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="tenant-modal__head">
                <h2>{friendlyName} er tilføjet</h2>
                <p>Live-data hentes nu — det tager typisk under et minut.{exchangeStatus !== 'done' && ' Forbind Exchange Online senere fra tenantens redigér-panel, når som helst.'}</p>
              </div>
              <div className="tenant-modal__foot" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn-primary" onClick={handleClose}>Luk</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckIcon() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>;
}
function SpinIcon() {
  return <svg className="spin" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>;
}
